import { Stack } from '@mui/material';
import Button from '@mui/material/Button';
import { Theme } from '@mui/material/styles';
import * as React from 'react';
import { shallowEqual } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { useClientFields } from '../../../../../sic-lib/api/hooks/useClientFields';
import {
    ConversationGridFiltersReducer,
    conversationGridFiltersReducer,
} from '../../../../../sic-lib/reducers/conversationGridFiltersReducer';
import { GenericReducer, genericReducer } from '../../../../../sic-lib/reducers/genericReducer';
import {
    ContactListFilterItem,
    buildContactListFilterItem,
} from '../../../../../sic-lib/types/ContactListFilterItem';
import { ListItem, ListType } from '../../../../../sic-lib/types/Lists';
import { IErrorFormValidation } from '../../../../../sic-lib/utils/formValidationHelpers';
import { requestContacts, setFilters } from '../../../../../store/conversationGrid';
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks';
import { DrawerCommon } from '../../../../Shared/Drawer/DrawerCommon';
import DialogConfirmUpdateGroup from '../../DialogConfirmUpdateGroup/DialogConfirmUpdateGroup';
import DialogCreateFilteredGroup from '../../DialogCreateFilteredGroup/DialogCreateFilteredGroup';
import ButtonSaveFilters from '../ButtonSaveFilters/ButtonSaveFilters';
import ConversationGridFiltersList from '../ConversationGridFiltersList/ConversationGridFiltersList';
import MenuAddFilterGroup from '../MenuAddFilterGroup/MenuAddFilterGroup';

const useStyles = makeStyles()((theme: Theme) => ({}));

type DialogState = {
    dialogId: string;
    validatedFilters: ContactListFilterItem[];
};

type Props = {
    drawerId: string;
    isOpen: boolean;
    onCloseHandler: () => void;
    list: ListItem;
};

export default function DrawerConversationGridFiltersRaw({
    drawerId,
    isOpen,
    onCloseHandler,
    list,
}: Props) {
    const { classes } = useStyles();
    const dispatch = useAppDispatch();

    const { data: clientFields } = useClientFields();

    const store = useAppSelector(state => {
        return {
            appliedFilters: state.conversationGrid?.filters || [],
        };
    }, shallowEqual);

    const [dialogState, dialogDispatch] = React.useReducer<GenericReducer<DialogState>>(
        genericReducer,
        {
            dialogId: '',
            validatedFilters: [],
        },
    );

    const handleOpenDialog = (event: React.MouseEvent<HTMLElement>) => {
        const { dialogId } = event.currentTarget.dataset;

        if (!dialogId) return;

        try {
            const mappedFilters = formState.filters.map(buildContactListFilterItem);

            dialogDispatch({
                dialogId: dialogId,
                validatedFilters: mappedFilters,
            });
        } catch (error: any) {
            if (error.isFormError) {
                formDispatch({
                    type: 'SET_FORM_ERROR',
                    formError: error as IErrorFormValidation,
                });
            }
        }
    };

    const handleCloseDialog = () => {
        /* ensure all dialog state is reset anytime a dialog closes */
        dialogDispatch({
            dialogId: '',
            validatedFilters: [],
        });
        //close or apply and close?
        onCloseHandler();
    };

    const [formState, formDispatch] = React.useReducer<ConversationGridFiltersReducer>(
        conversationGridFiltersReducer,
        {
            fields: [],
            filters: [],
            formError: null,
        },
    );

    React.useEffect(() => {
        formDispatch({
            type: 'SET_FILTERABLE_FIELDS',
            fields: clientFields || [],
        });
    }, [clientFields]);

    React.useEffect(() => {
        formDispatch({
            type: 'SET_FILTERS_FROM_STATE',
            filters: store.appliedFilters,
        });
    }, [store.appliedFilters]);

    const handleClearFilters = () => {
        formDispatch({ type: 'CLEAR_FORM_FILTERS' });
    };

    const handleApplyFilters = () => {
        try {
            const mappedFilters = formState.filters.map(buildContactListFilterItem);

            formDispatch({
                type: 'SET_FORM_ERROR',
                formError: null,
            });

            dispatch(setFilters({ filters: mappedFilters }));
            dispatch(requestContacts());

            onCloseHandler();
        } catch (error: any) {
            if (error.isFormError) {
                formDispatch({
                    type: 'SET_FORM_ERROR',
                    formError: error as IErrorFormValidation,
                });
            }
        }
    };

    return (
        <React.Fragment>
            <DrawerCommon
                open={isOpen}
                handleOnClose={onCloseHandler}
                width="500px"
                title="Group Contact Filters"
                footer={
                    <Stack direction="row" spacing={2} p={2}>
                        <Button onClick={onCloseHandler} variant="outlined">
                            Cancel
                        </Button>
                        <Button variant="outlined" color="secondary" onClick={handleApplyFilters}>
                            Apply
                        </Button>
                        <ButtonSaveFilters
                            isDynamicList={list.type === ListType.dynamic}
                            isDisabled={formState.filters.length < 1}
                            onOpenDialogHandler={handleOpenDialog}
                        />
                    </Stack>
                }
            >
                <Stack p={2} spacing={2}>
                    <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <MenuAddFilterGroup
                            fields={formState.fields}
                            filters={formState.filters}
                            formDispatch={formDispatch}
                        />
                        <Button onClick={handleClearFilters}>Clear</Button>
                    </Stack>
                    <ConversationGridFiltersList
                        filters={formState.filters}
                        formDispatch={formDispatch}
                        formError={formState.formError}
                    />
                </Stack>
            </DrawerCommon>
            {
                /*
                    TIP: dialogs do not unmount when closed(?) persevering state: force
                    an unmount with conditional rendering
                 */
                dialogState.dialogId === 'create_filter_group' && (
                    <DialogCreateFilteredGroup
                        isOpen={dialogState.dialogId === 'create_filter_group'}
                        filters={dialogState.validatedFilters}
                        onCloseHandler={handleCloseDialog}
                    />
                )
            }
            {dialogState.dialogId === 'update_filter_group' && (
                <DialogConfirmUpdateGroup
                    isOpen={dialogState.dialogId === 'update_filter_group'}
                    listItem={list}
                    listFilters={dialogState.validatedFilters}
                    onCloseHandler={handleCloseDialog}
                />
            )}
        </React.Fragment>
    );
}
